/**
 * ehr#437 (GROUP_NOTE_10-14-V26, V73 101 A): what a group member's note needs before sign-off,
 * beyond the progress note's own checks: their Participation and Mood (unless they weren't there),
 * and no other member's name in their note. The server checks the same.
 */
import { namedMembers, type GroupAttendeeView } from '@ehr/shared';
import type { SignCheck } from './noteV81.js';

export function groupSignChecks(n: {
  present: boolean;
  participation: string;
  mood: string;
  /** The note's text by box id. */
  text: Record<string, string>;
  /** The other members' first names. */
  members: readonly string[];
  /** ehr#571 round 2 (V22): the session has a Group summary (false asks for it). */
  summaryWritten?: boolean;
  /** V22 groupOwnItems: the Group summary and the other members' notes, and this member's name. */
  own?: { firstName: string; shared: readonly string[] };
}): SignCheck[] {
  const out: SignCheck[] = [];
  if (n.present && !n.participation) out.push({ label: 'Participation', target: 'groupfields' });
  if (n.present && !n.mood) out.push({ label: 'Mood', target: 'groupfields' });
  if (n.present && n.summaryWritten === false) out.push({ label: 'Group summary', target: 'gsum' });
  for (const [box, t] of Object.entries(n.text)) {
    const named = namedMembers(t, n.members);
    if (named.length)
      out.push({ label: `Take out ${named.join(' and ')} (another member)`, target: `sec-${box}` });
  }
  const box = n.present && n.own ? ownSentenceMissing(n.text, n.own.shared) : null;
  if (box && n.own)
    out.push({ label: `One sentence only about ${n.own.firstName}`, target: `sec-${box}` });
  return out;
}

/**
 * V26 roster groups: who still needs work, who is ready, who is signed. ehr#689 (Family V15 / Group
 * V25): a note with the reviewer sits under Pending review, not Signed, until it is co-signed.
 */
export type RosterGroup = 'needs' | 'ready' | 'pending' | 'signed';
export function rosterGroup(a: Pick<GroupAttendeeView, 'note'>): RosterGroup {
  const st = a.note?.status ?? 'draft';
  if (st === 'pending') return 'pending';
  if (st === 'signed' || st === 'cosigned') return 'signed';
  const b = a.note?.body ?? {};
  const absent = (b.attendance as { status?: string } | undefined)?.status === 'Not present';
  const written = [
    'subjective',
    'objective',
    'assessment',
    'plan',
    'data',
    'behavior',
    'intervention',
  ].some((k) => typeof b[k] === 'string' && (b[k] as string).trim());
  if (st === 'draft' && (absent || (written && b.participation && b.groupMood))) return 'ready';
  return 'needs';
}

/** First names of everyone in the group but this member (for the name check). */
export function otherMembers(
  attendees: Pick<GroupAttendeeView, 'contactId' | 'name'>[],
  me: string,
) {
  return attendees
    .filter((a) => a.contactId !== me)
    .map((a) => a.name.split(/\s+/)[0] ?? '')
    .filter((n) => n.length > 1);
}

/** The note's sentences, for "One sentence only about ..." (V22 cloneSents). */
export function sentences(text: string): string[] {
  return text
    .split(/(?<=[.!?])\s+|\n+/)
    .map((t) => t.replace(/\s+/g, ' ').trim().toLowerCase())
    .filter((t) => t.length > 0);
}

/**
 * V22 groupOwnItems: a member's note needs at least one sentence that is only about them: one that
 * isn't also in the Group summary or another member's note. The box to go to, or null when it has
 * one (or nothing is written yet, which the boxes' own checks cover).
 */
export function ownSentenceMissing(
  text: Record<string, string>,
  shared: readonly string[],
): string | null {
  const boxes = Object.entries(text).filter(([, t]) => t.trim());
  if (!boxes.length) return null;
  const seen = new Set(shared.flatMap(sentences));
  const own = boxes.some(([, t]) => sentences(t).some((s) => !seen.has(s)));
  return own ? null : boxes[0]![0];
}

/**
 * ehr#571 round 2 (GROUP_NOTE_10-20-V22 groupRoughFor): what one member's note drafts from: the
 * group's notes without the sentences that name another member (one client's details never land in
 * another client's note), then that member's own notes.
 */
export function groupTextFor(groupText: string, others: readonly string[], own: string): string {
  const keep = groupText
    .split(/(?<=[.!?])\s+|\n+/)
    .map((t) => t.trim())
    .filter((t) => t && !namedMembers(t, others).length);
  return [...keep, own.trim()].filter(Boolean).join(' ');
}
